import {DeleteOutlined, CloseOutlined} from '@ant-design/icons';
import {
  ExecutionSetupDTO,
  ExecutionSetupType,
  ExternalUserType,
  ModelAccessRequest,
  ModelDTO,
  UserModelAccess,
  ExUserRegistrationDetails,
} from 'hemwb-api';
import React, {useMemo, useState} from 'react';
import {
  Button,
  Checkbox,
  Divider,
  Empty,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Spin,
  Steps,
  Table,
  Typography,
} from 'antd';
import {ColumnsType} from 'antd/es/table';
import {useNavigate} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import {MRAppRoutes} from '../../../../router/routes';
import {messageAdd, MessageTypes} from '../../../../../store/message';
import {useExecutionSetupList} from '../../../../../store/executionSetup';
import {useModelEligibleList} from '../../../../../store/model';
import {registerExternalUser} from '../../../../../store/user';
import {useLastValidValue} from '../../../../../hooks/useLastValidValue';
import {fullName} from '../../../../../store/user/utils';
import {hardCodedOptions} from './constants';
import {ExternalUserWithModel} from './types';
import {getExternalUserInputTypeLabel} from './utils';

type UserEnrollFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  userType?: ExternalUserType;
  purchaseOrder?: number | null;
  requestInfo: string;
};

type SelectedSubModel = {
  modelUuid: string;
  modelName: string;
  role: string;
  subModel: ExecutionSetupDTO;
};

const stepItems = ['Profile', 'Access', 'Model Roles', 'Management', 'Review'].map((title) => ({title}));
const webModelPlatform = 'WebModel 2.0';
const merckDomainPattern = /@(merck|msd)\.com$/i;
const emailPattern = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

const initialUserValues: UserEnrollFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  userType: undefined,
  purchaseOrder: null,
  requestInfo: '',
};

const getRoleRequest = (role?: string) => {
  if (role === 'Editor') {
    return UserModelAccess.OWNER;
  }

  if (role === 'Model Viewer') {
    return UserModelAccess.MODELVIEWER;
  }

  if (role === 'Calibration Modeler') {
    return UserModelAccess.CALIBRATORMODELER;
  }

  if (role === 'Scenario Modeler') {
    return UserModelAccess.SCENARIOMODELER;
  }

  if (role === 'Model Display Viewer') {
    return UserModelAccess.DISPLAYVIEWER;
  }

  return UserModelAccess.MODELVIEWER;
};

const getPlatform = (model: ModelDTO) => model.metadata.platform ?? '';

const buildModelSelection = (
  modelUuids: string[],
  availableModels: ModelDTO[],
  existingSelections: ExternalUserWithModel[],
): ExternalUserWithModel[] => {
  const existingByUuid = new Map(existingSelections.map((item) => [item.uuid, item]));

  return modelUuids.reduce<ExternalUserWithModel[]>((result, uuid) => {
    const model = availableModels.find((item) => item.uuid === uuid);

    if (!model) {
      return result;
    }

    result.push({
      uuid: model.uuid,
      name: model.name,
      platform: getPlatform(model),
      role: existingByUuid.get(model.uuid)?.role,
    });

    return result;
  }, []);
};

const getSelectPopupContainer = () => document.body;

const TO_LOCALE_DATE_STRING_OPTIONS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

const ExternalUserEnroll: React.FC<{selectedUser?: ExUserRegistrationDetails | null; onClose?: () => void}> = ({
  selectedUser,
  onClose,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [bulkRole, setBulkRole] = useState<string>();
  const [currentWebModelIndex, setCurrentWebModelIndex] = useState(0);
  const [selectedRoleRows, setSelectedRoleRows] = useState<Record<string, boolean>>({});
  const [profileValues, setProfileValues] = useState<UserEnrollFormValues>(() => {
    if (selectedUser) {
      return {
        firstName: selectedUser.firstName || '',
        lastName: selectedUser.lastName || '',
        email: selectedUser.email || '',
        userType: undefined,
        purchaseOrder: null,
        requestInfo: '',
      };
    }
    return initialUserValues;
  });
  const [selectedModels, setSelectedModels] = useState<ExternalUserWithModel[]>([]);
  const [selectedSubModelsByModel, setSelectedSubModelsByModel] = useState<Record<string, SelectedSubModel[]>>({});

  const eligibleModels = useModelEligibleList(profileValues.userType ?? ExternalUserType.VENDOR);
  const webModels = useMemo(
    () => selectedModels.filter((model) => model.platform === webModelPlatform),
    [selectedModels],
  );
  const currentWebModel = webModels[currentWebModelIndex];

  const calibrationList = useExecutionSetupList(currentWebModel?.uuid ?? '', ExecutionSetupType.CALIBRATION);
  const scenarioList = useExecutionSetupList(currentWebModel?.uuid ?? '', ExecutionSetupType.SCENARIO);
  const calibrationStoredList = useLastValidValue(calibrationList) ?? [];
  const scenarioStoredList = useLastValidValue(scenarioList) ?? [];

  const emailError = useMemo(() => {
    if (!profileValues.email) {
      return 'This field is required.';
    }

    if (!emailPattern.test(profileValues.email)) {
      return 'Enter a valid email address.';
    }

    if (merckDomainPattern.test(profileValues.email)) {
      return 'You have entered email address ending with the Merck domain. Please enter an email address that does not have the Merck domain such as @merck.com or @msd.com.';
    }

    return '';
  }, [profileValues.email]);

  const isProfileValid =
    Boolean(profileValues.firstName.trim()) &&
    Boolean(profileValues.lastName.trim()) &&
    Boolean(profileValues.userType) &&
    !emailError &&
    (profileValues.userType !== ExternalUserType.VENDOR || Boolean(profileValues.purchaseOrder)) &&
    (profileValues.userType !== ExternalUserType.OTHER || Boolean(profileValues.requestInfo.trim()));

  const isAccessValid = selectedModels.length > 0;
  const isRoleValid = selectedModels.length > 0 && selectedModels.every((model) => Boolean(model.role));
  const selectedRoleCount = selectedModels.filter((model) => selectedRoleRows[model.uuid]).length;
  const currentSubModelRows = selectedSubModelsByModel[currentWebModel?.uuid ?? ''] ?? [];
  const currentSubModelKeys = currentSubModelRows
    .map((item) => item.subModel.id)
    .filter((id): id is React.Key => Boolean(id));

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(MRAppRoutes.EXTERNALIZATION_LIST);
    }
  };

  const handleProfileChange = <K extends keyof UserEnrollFormValues>(field: K, value: UserEnrollFormValues[K]) => {
    setProfileValues((previous) => {
      const nextValues = {
        ...previous,
        [field]: value,
      };

      if (field === 'userType') {
        return {
          ...nextValues,
          purchaseOrder: value === ExternalUserType.VENDOR ? previous.purchaseOrder : null,
          requestInfo: value === ExternalUserType.OTHER ? previous.requestInfo : '',
        };
      }

      return nextValues;
    });
  };

  const handleModelSelectionChange = (modelUuids: string[]) => {
    if (!eligibleModels) {
      return;
    }

    const nextSelections = buildModelSelection(modelUuids, eligibleModels, selectedModels);
    const nextSelectionKeys = new Set(nextSelections.map((model) => model.uuid));

    setSelectedModels(nextSelections);
    setSelectedRoleRows((previous) =>
      Object.fromEntries(Object.entries(previous).filter(([uuid]) => nextSelectionKeys.has(uuid))),
    );
    setSelectedSubModelsByModel((previous) =>
      Object.fromEntries(Object.entries(previous).filter(([uuid]) => nextSelectionKeys.has(uuid))),
    );
    setCurrentWebModelIndex(0);
  };

  const updateRole = (uuid: string, role?: string) => {
    setSelectedModels((previous) => previous.map((item) => (item.uuid === uuid ? {...item, role} : item)));

    if (role === 'Model Owner') {
      setSelectedSubModelsByModel((previous) => {
        const nextValues = {...previous};
        delete nextValues[uuid];
        return nextValues;
      });
    }
  };

  const removeModel = (uuid: string) => {
    setSelectedModels((previous) => previous.filter((item) => item.uuid !== uuid));
    setSelectedRoleRows((previous) => {
      const nextValues = {...previous};
      delete nextValues[uuid];
      return nextValues;
    });
    setSelectedSubModelsByModel((previous) => {
      const nextValues = {...previous};
      delete nextValues[uuid];
      return nextValues;
    });
    setCurrentWebModelIndex(0);
  };

  const applyBulkRole = (role: string) => {
    setBulkRole(role);
    setSelectedModels((previous) => previous.map((item) => (selectedRoleRows[item.uuid] ? {...item, role} : item)));
  };

  const updateCurrentSubModels = (subModelIds: React.Key[], tableType?: 'calibration' | 'scenario') => {
    if (!currentWebModel) {
      return;
    }

    const selectedIds = new Set(subModelIds.map((id) => Number(id)));

    // Get the IDs of calibration and scenario items for filtering
    const calibrationIds = new Set(calibrationStoredList?.map((item) => item.id) ?? []);
    const scenarioIds = new Set(scenarioStoredList?.map((item) => item.id) ?? []);

    setSelectedSubModelsByModel((previous) => {
      const existingSelections = previous[currentWebModel.uuid] ?? [];

      // Determine which table is being updated based on tableType
      // If tableType is not provided, we'll determine it from which IDs are being updated
      let isUpdatingCalibration = tableType === 'calibration';
      let isUpdatingScenario = tableType === 'scenario';

      if (!tableType) {
        // Fallback: determine from selectedIds
        const hasCalibrationIds = Array.from(selectedIds).some((id) => calibrationIds.has(id));
        const hasScenarioIds = Array.from(selectedIds).some((id) => scenarioIds.has(id));
        isUpdatingCalibration = hasCalibrationIds;
        isUpdatingScenario = hasScenarioIds;
      }

      // Start with selections from tables that are NOT being updated
      let preservedSelections = existingSelections;
      if (isUpdatingCalibration) {
        // Remove all calibration selections, keep only scenario selections
        preservedSelections = existingSelections.filter((item) => scenarioIds.has(item.subModel.id));
      } else if (isUpdatingScenario) {
        // Remove all scenario selections, keep only calibration selections
        preservedSelections = existingSelections.filter((item) => calibrationIds.has(item.subModel.id));
      }

      // Build new selections from the selected IDs
      const allItems = [...calibrationStoredList, ...scenarioStoredList];
      const newSelections = allItems
        .filter((item) => item.id && selectedIds.has(item.id))
        .map((item) => ({
          modelUuid: currentWebModel.uuid,
          modelName: currentWebModel.name,
          role: currentWebModel.role ?? '',
          subModel: item,
        }));

      // Combine preserved selections with new selections
      const combinedSelections = [...preservedSelections, ...newSelections];

      // Deduplicate by subModel.id to prevent duplicate entries
      const seenIds = new Set<number>();
      const finalSelections = combinedSelections.filter((item) => {
        if (seenIds.has(item.subModel.id)) {
          return false;
        }
        seenIds.add(item.subModel.id);
        return true;
      });

      return {
        ...previous,
        [currentWebModel.uuid]: finalSelections,
      };
    });
  };

  const handleNext = () => {
    if (activeStep === 0 && isProfileValid) {
      setActiveStep(1);
      return;
    }

    if (activeStep === 1 && isAccessValid) {
      setActiveStep(2);
      return;
    }

    if (activeStep === 2 && isRoleValid) {
      setCurrentWebModelIndex(0);
      setActiveStep(webModels.length > 0 ? 3 : 4);
      return;
    }

    if (activeStep === 3) {
      if (currentWebModelIndex < webModels.length - 1) {
        setCurrentWebModelIndex((previous) => previous + 1);
      } else {
        setActiveStep(4);
      }
    }
  };

  const handlePrevious = () => {
    if (activeStep === 0) {
      handleClose();
      return;
    }

    if (activeStep === 4) {
      setActiveStep(webModels.length > 0 ? 3 : 2);
      if (webModels.length > 0) {
        setCurrentWebModelIndex(Math.max(webModels.length - 1, 0));
      }
      return;
    }

    if (activeStep === 3) {
      if (currentWebModelIndex > 0) {
        setCurrentWebModelIndex((previous) => previous - 1);
      } else {
        setActiveStep(2);
      }
      return;
    }

    setActiveStep((previous) => previous - 1);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      const modelAccesses: ModelAccessRequest[] = selectedModels.map((model) => ({
        modelUuid: model.uuid,
        modelRole: getRoleRequest(model.role),
      }));
      const purchaseOrderId = typeof profileValues.purchaseOrder === 'number' ? profileValues.purchaseOrder : undefined;
      const purchaseOrderDetails = {
        purchaseOrderId,
        userRequest: profileValues.requestInfo,
      };
      const selectedSubModelIds = Object.values(selectedSubModelsByModel)
        .flat()
        .map((item) => item.subModel.id)
        .filter((id): id is number => Boolean(id));

      await registerExternalUser(dispatch, {
        firstname: profileValues.firstName.trim(),
        lastname: profileValues.lastName.trim(),
        email: profileValues.email.toLowerCase(),
        modelAccesses,
        subType: profileValues.userType as ExternalUserType,
        subModelIds: selectedSubModelIds,
        purchaseOrderDetails,
      });

      dispatch(
        messageAdd('Registration code has been emailed to the external user successfully', MessageTypes.SUCCESS),
      );
      if (onClose) {
        onClose();
      } else {
        navigate(MRAppRoutes.EXTERNALIZATION_LIST);
      }
    } catch {
      dispatch(messageAdd('Failed to send registration invite.', MessageTypes.ERROR));
    } finally {
      setSubmitting(false);
    }
  };

  // Get filtered roles based on user type and model platform
  const getAvailableRoles = (model?: ExternalUserWithModel) => {
    // If no model provided → bulk selection scenario
    if (!model) {
      const isMRModelPresent = selectedModels.some((m) => m.platform === 'Model Repository');

      if (isMRModelPresent) {
        // If ANY MR model selected, show only MR roles for safety
        return hardCodedOptions.modelRoles.slice(0, 2);
      } else {
        // No MR models - use user type filtering
        if (profileValues.userType === ExternalUserType.HTA) {
          return hardCodedOptions.modelRoles.slice(2, 5);
        } else {
          return hardCodedOptions.modelRoles;
        }
      }
    }

    // Single model case (per-row in table)
    const isMRModel = model.platform === 'Model Repository';

    if (isMRModel) {
      return hardCodedOptions.modelRoles.slice(0, 2);
    } else {
      if (profileValues.userType === ExternalUserType.HTA) {
        return hardCodedOptions.modelRoles.slice(2, 5);
      } else {
        return hardCodedOptions.modelRoles;
      }
    }
  };

  const roleColumns: ColumnsType<ExternalUserWithModel> = [
    {
      title: 'Model Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: (
        <span>
          Select the Model Role
          <div style={{fontWeight: 'normal'}}>
            Select model roles for the external users based on the selected models.
          </div>
        </span>
      ),
      dataIndex: 'role',
      key: 'role',
      render: (_, record) => (
        <Select
          allowClear
          placeholder="Select"
          style={{width: '100%'}}
          getPopupContainer={getSelectPopupContainer}
          styles={{popup: {root: {zIndex: 2000}}}}
          value={record.role}
          options={getAvailableRoles(record).map((role) => ({label: role, value: role}))}
          onChange={(value) => updateRole(record.uuid, value)}
        />
      ),
    },
    {
      title: (
        <Checkbox
          checked={selectedModels.length > 0 && selectedModels.every((model) => selectedRoleRows[model.uuid])}
          indeterminate={selectedRoleCount > 0 && selectedRoleCount < selectedModels.length}
          onChange={(event) => {
            const checked = event.target.checked;
            setSelectedRoleRows(Object.fromEntries(selectedModels.map((model) => [model.uuid, checked])));
          }}
        >
          Select All
        </Checkbox>
      ),
      key: 'selected',
      width: 160,
      render: (_, record) => (
        <Checkbox
          checked={Boolean(selectedRoleRows[record.uuid])}
          onChange={(event) =>
            setSelectedRoleRows((previous) => ({
              ...previous,
              [record.uuid]: event.target.checked,
            }))
          }
        />
      ),
    },
    {
      title: '',
      key: 'delete',
      width: 72,
      render: (_, record) => <Button type="text" icon={<DeleteOutlined />} onClick={() => removeModel(record.uuid)} />,
    },
  ];

  const subModelColumns: ColumnsType<ExecutionSetupDTO> = [
    {
      title: 'Sub-Model Type',
      key: 'type',
      render: (_, record) =>
        record.type ? record.type.charAt(0).toUpperCase() + record.type.slice(1).toLowerCase() : '-',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Created By',
      key: 'author',
      render: (_, record) => (record.createdBy ? fullName(record.createdBy) : '-'),
    },
    {
      title: 'Created',
      key: 'updatedAt',
      render: (_, record) =>
        record.createdOn ? new Date(record.createdOn).toLocaleDateString('en-US', TO_LOCALE_DATE_STRING_OPTIONS) : '-',
    },
  ];

  const getSubModelColumns = (dataSource: ExecutionSetupDTO[]): ColumnsType<ExecutionSetupDTO> => {
    const allItemsSelected =
      dataSource.length > 0 && dataSource.every((item) => currentSubModelKeys.includes(item.id!));
    const someItemsSelected = dataSource.some((item) => currentSubModelKeys.includes(item.id!));

    // Determine which table type this is
    const isCalibration = dataSource === calibrationStoredList;
    const tableType: 'calibration' | 'scenario' = isCalibration ? 'calibration' : 'scenario';

    const handleSelectAllChange = (_e: any) => {
      const dataSourceIds = new Set(dataSource.map((item) => item.id!));
      const isCurrentlyAllSelected = allItemsSelected;

      if (isCurrentlyAllSelected) {
        // Currently all selected → deselect all
        const newKeys = currentSubModelKeys.filter((key) => !dataSourceIds.has(key));
        updateCurrentSubModels(newKeys, tableType);
      } else {
        // Currently nothing or some selected → select all
        const newKeys = Array.from(new Set([...currentSubModelKeys, ...dataSource.map((item) => item.id!)]));
        updateCurrentSubModels(newKeys, tableType);
      }
    };

    return [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Created By',
        key: 'author',
        render: (_, record) => (record.createdBy ? fullName(record.createdBy) : '-'),
      },
      {
        title: 'Created',
        key: 'updatedAt',
        render: (_, record) =>
          record.createdOn
            ? new Date(record.createdOn).toLocaleDateString('en-US', TO_LOCALE_DATE_STRING_OPTIONS)
            : '-',
      },
      {
        title: (
          <Checkbox
            checked={allItemsSelected}
            indeterminate={someItemsSelected && !allItemsSelected}
            onChange={handleSelectAllChange}
          />
        ),
        key: 'checkbox',
        width: 50,
        render: (_, record) => (
          <Checkbox
            checked={currentSubModelKeys.includes(record.id!)}
            onChange={(e) => {
              const newKeys = e.target.checked
                ? [...currentSubModelKeys, record.id!]
                : currentSubModelKeys.filter((key) => key !== record.id!);
              updateCurrentSubModels(newKeys, tableType);
            }}
          />
        ),
      },
    ];
  };

  const reviewColumns: ColumnsType<ExternalUserWithModel> = [
    {
      title: 'Model Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Assign Model Role',
      dataIndex: 'role',
      key: 'role',
      render: (value) => value ?? '-',
    },
  ];

  const reviewSubModelGroups = webModels.map((model) => ({
    model,
    rows: selectedSubModelsByModel[model.uuid] ?? [],
  }));

  const renderProfileStep = () => (
    <div style={{display: 'grid', gap: 24}}>
      <Typography.Text>
        Enter the external user&apos;s information to generate a registration code for signing up the external user.
      </Typography.Text>
      <div style={{display: 'grid', gap: 24, gridTemplateColumns: 'minmax(0, 1fr) minmax(280px, 340px)'}}>
        <div style={{display: 'grid', gap: 16}}>
          <div>
            <Typography.Text>
              First Name <span style={{color: '#ff4d4f'}}>*</span>
            </Typography.Text>
            <Input
              placeholder="First Name"
              value={profileValues.firstName}
              onChange={(event) => handleProfileChange('firstName', event.target.value)}
            />
          </div>
          <div>
            <Typography.Text>
              Last Name <span style={{color: '#ff4d4f'}}>*</span>
            </Typography.Text>
            <Input
              placeholder="Last Name"
              value={profileValues.lastName}
              onChange={(event) => handleProfileChange('lastName', event.target.value)}
            />
          </div>
          <div>
            <Typography.Text>
              Email ID <span style={{color: '#ff4d4f'}}>*</span>
            </Typography.Text>
            <Input
              status={profileValues.email && emailError ? 'error' : ''}
              placeholder="Enter Email ID"
              value={profileValues.email}
              onChange={(event) => handleProfileChange('email', event.target.value)}
            />
            {profileValues.email && emailError ? (
              <Typography.Text type="danger" style={{display: 'block', marginTop: 8}}>
                {emailError}
              </Typography.Text>
            ) : null}
          </div>
          {profileValues.userType === ExternalUserType.VENDOR ? (
            <div>
              <Typography.Text>Provide the purchase order #</Typography.Text>
              <InputNumber
                style={{width: '100%'}}
                placeholder="Enter numeric purchase order #"
                value={profileValues.purchaseOrder ?? undefined}
                onChange={(value) => handleProfileChange('purchaseOrder', value ?? null)}
              />
            </div>
          ) : null}
          {profileValues.userType === ExternalUserType.OTHER ? (
            <div>
              <Typography.Text>Provide more information about your request</Typography.Text>
              <Input.TextArea
                rows={4}
                placeholder="Enter reason"
                value={profileValues.requestInfo}
                onChange={(event) => handleProfileChange('requestInfo', event.target.value)}
              />
            </div>
          ) : null}
        </div>
        <div style={{padding: 20, borderLeft: '1px solid #e0e6e8'}}>
          <Typography.Text>
            What type of external user is this? <span style={{color: '#ff4d4f'}}>*</span>
          </Typography.Text>
          <Radio.Group
            style={{display: 'grid', gap: 12, marginTop: 16}}
            value={profileValues.userType}
            onChange={(event) => handleProfileChange('userType', event.target.value)}
          >
            {[ExternalUserType.VENDOR, ExternalUserType.HTA, ExternalUserType.OTHER].map((option) => (
              <Radio key={option} value={option}>
                {getExternalUserInputTypeLabel(option)}
              </Radio>
            ))}
          </Radio.Group>
        </div>
      </div>
    </div>
  );

  const renderAccessStep = () => (
    <div style={{display: 'grid', gap: 20}}>
      <Typography.Text>Enable external user access to specific models by selecting and assigning them.</Typography.Text>
      {!eligibleModels ? (
        <div style={{display: 'flex', justifyContent: 'center', padding: '48px 0'}}>
          <Spin />
        </div>
      ) : (
        <>
          <div style={{maxWidth: 520}}>
            <Typography.Text>
              Select specific model <span style={{color: '#ff4d4f'}}>*</span>
            </Typography.Text>
            <Select
              mode="multiple"
              style={{width: '100%', marginTop: 8}}
              placeholder="Select model"
              showSearch
              listHeight={320}
              optionFilterProp="label"
              filterOption={(input, option) =>
                String(option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              getPopupContainer={getSelectPopupContainer}
              styles={{popup: {root: {zIndex: 2000}}}}
              notFoundContent="No models found"
              optionRender={(option) => {
                const checked = selectedModels.some((model) => model.uuid === option.data.value);

                return (
                  <div style={{display: 'flex', alignItems: 'center', gap: 8, pointerEvents: 'none'}}>
                    <Checkbox checked={checked} />
                    <span>{String(option.data.label ?? '')}</span>
                  </div>
                );
              }}
              value={selectedModels.map((model) => model.uuid)}
              options={eligibleModels.map((model) => ({label: model.name, value: model.uuid}))}
              onChange={handleModelSelectionChange}
            />
          </div>
          {selectedModels.length > 0 ? (
            <div style={{padding: 16, borderRadius: 16, background: '#f8fbff', border: '1px solid #d9e2f2'}}>
              <Typography.Text>Selected Models</Typography.Text>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12}}>
                {selectedModels.map((model) => (
                  <div
                    key={model.uuid}
                    style={{padding: '8px 12px', borderRadius: 999, background: '#ffffff', border: '1px solid #d9e2f2'}}
                  >
                    {model.name}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No models selected" />
          )}
        </>
      )}
    </div>
  );

  const renderRoleStep = () => (
    <div style={{display: 'grid', gap: 20}}>
      <Typography.Text>Select model roles for the extenal users based on the selected models.</Typography.Text>
      {selectedRoleCount > 0 ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 16,
            borderRadius: 16,
            background: '#f8fbff',
            border: '1px solid #d9e2f2',
          }}
        >
          <Typography.Text>{selectedRoleCount} model(s) selected for bulk update</Typography.Text>
          <Select
            placeholder="Bulk assign role"
            style={{width: 220}}
            getPopupContainer={getSelectPopupContainer}
            styles={{popup: {root: {zIndex: 2000}}}}
            value={bulkRole}
            options={getAvailableRoles().map((role) => ({label: role, value: role}))}
            onChange={(value) => value && applyBulkRole(value)}
          />
        </div>
      ) : null}
      <Table
        columns={roleColumns}
        dataSource={selectedModels}
        pagination={false}
        rowKey="uuid"
        locale={{emptyText: 'No models selected'}}
      />
    </div>
  );

  const renderManagementStep = () => {
    if (!currentWebModel) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No web models selected" />;
    }

    const isModelOwner = currentWebModel.role === 'Model Owner';
    const isEditorRole = currentWebModel.role === 'Editor';
    const currentStepLabel = `${currentWebModelIndex + 1} of ${webModels.length}`;

    return (
      <div style={{display: 'grid', gap: 20}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <div>
            <Typography.Title level={5} style={{marginBottom: '10px', color: '#00857a', fontWeight: 'normal'}}>
              {currentWebModel.name}
            </Typography.Title>
            <Typography.Text>Select sub models for external users based on the selected models.</Typography.Text>
          </div>
          <Typography.Text>{currentStepLabel}</Typography.Text>
        </div>
        {isModelOwner ? (
          <div style={{padding: 16, borderRadius: 16, background: '#f6ffed', border: '1px solid #b7eb8f'}}>
            <Typography.Text>
              This user is assigned as Model Owner. Calibration, scenario, and display access will be granted
              automatically.
            </Typography.Text>
          </div>
        ) : isEditorRole ? (
          <div style={{padding: 16, borderRadius: 16, background: '#f6ffed', border: '1px solid #b7eb8f'}}>
            <Typography.Text>
              For this model, you have selected an Editor role, so the External User will get access to all the Sub
              models. Click Next to proceed to the next screen or click Cancel to change the role.
            </Typography.Text>
          </div>
        ) : (
          <Spin spinning={Boolean(currentWebModel.uuid) && (!calibrationList || !scenarioList)}>
            <div style={{display: 'grid', gap: 20}}>
              <div>
                <Typography.Title level={5} style={{marginBottom: '10px', color: '#00857a', fontWeight: 'normal'}}>
                  Calibration Model Access
                </Typography.Title>
                <Table
                  rowKey="id"
                  columns={getSubModelColumns(calibrationStoredList)}
                  dataSource={calibrationStoredList}
                  pagination={false}
                  locale={{emptyText: 'No calibration submodels found'}}
                />
              </div>
              <div>
                <Typography.Title level={5} style={{marginBottom: '10px', color: '#00857a', fontWeight: 'normal'}}>
                  Scenario Model Access
                </Typography.Title>
                <Table
                  rowKey="id"
                  columns={getSubModelColumns(scenarioStoredList)}
                  dataSource={scenarioStoredList}
                  pagination={false}
                  locale={{emptyText: 'No scenario submodels found'}}
                />
              </div>
            </div>
          </Spin>
        )}
      </div>
    );
  };

  const renderReviewStep = () => (
    <div style={{display: 'grid', gap: 24}}>
      {/* User Information Section */}
      <div style={{display: 'grid', gap: 12}}>
        <div style={{display: 'grid', gridTemplateColumns: '250px 1fr', gap: 12}}>
          <Typography.Text strong>First Name:</Typography.Text>
          <Typography.Text>{profileValues.firstName || '-'}</Typography.Text>
        </div>
        <div style={{display: 'grid', gridTemplateColumns: '250px 1fr', gap: 12}}>
          <Typography.Text strong>Last Name:</Typography.Text>
          <Typography.Text>{profileValues.lastName || '-'}</Typography.Text>
        </div>
        <div style={{display: 'grid', gridTemplateColumns: '250px 1fr', gap: 12}}>
          <Typography.Text strong>Email Id:</Typography.Text>
          <Typography.Text>{profileValues.email || '-'}</Typography.Text>
        </div>
        <div style={{display: 'grid', gridTemplateColumns: '250px 1fr', gap: 12}}>
          <Typography.Text strong>Type of external user:</Typography.Text>
          <Typography.Text>
            {profileValues.userType ? getExternalUserInputTypeLabel(profileValues.userType) : '-'}
          </Typography.Text>
        </div>
        {profileValues.userType === ExternalUserType.VENDOR ? (
          <div style={{display: 'grid', gridTemplateColumns: '250px 1fr', gap: 12}}>
            <Typography.Text strong>Purchase Order #:</Typography.Text>
            <Typography.Text>{profileValues.purchaseOrder ?? '-'}</Typography.Text>
          </div>
        ) : null}
        {profileValues.userType === ExternalUserType.OTHER ? (
          <div style={{display: 'grid', gridTemplateColumns: '250px 1fr', gap: 12}}>
            <Typography.Text strong>More information about the request:</Typography.Text>
            <Typography.Text>{profileValues.requestInfo || '-'}</Typography.Text>
          </div>
        ) : null}
      </div>
      <Divider style={{margin: 0}} />
      <div style={{display: 'grid', gap: 16}}>
        <Table columns={reviewColumns} dataSource={selectedModels} pagination={false} rowKey="uuid" />
      </div>
      {reviewSubModelGroups.map(({model, rows}) => (
        <div key={model.uuid} style={{display: 'grid', gap: 12}}>
          <Typography.Title level={5} style={{margin: 0, color: '#00857a', fontWeight: 'normal'}}>
            {model.name}
          </Typography.Title>
          {model.role === 'Model Owner' ? (
            <Typography.Text type="secondary">
              For this model, you have selected a Editor role, so the External User will get access to all the Sub
              models. Click Next to proceed to the next screen or click Cancel to change the role.
            </Typography.Text>
          ) : model.role === 'Editor' ? (
            <Typography.Text type="secondary">
              For this model, you have selected a Editor role, so the External User will get access to all the Sub
              models. Click Next to proceed to the next screen or click Cancel to change the role.
            </Typography.Text>
          ) : (
            <Table
              columns={subModelColumns}
              dataSource={rows.map((item) => item.subModel)}
              pagination={false}
              rowKey="id"
              locale={{emptyText: 'No submodel access selected'}}
            />
          )}
        </div>
      ))}
    </div>
  );

  const renderStep = () => {
    if (activeStep === 0) {
      return renderProfileStep();
    }

    if (activeStep === 1) {
      return renderAccessStep();
    }

    if (activeStep === 2) {
      return renderRoleStep();
    }

    if (activeStep === 3) {
      return renderManagementStep();
    }

    return renderReviewStep();
  };

  const nextDisabled =
    (activeStep === 0 && !isProfileValid) || (activeStep === 1 && !isAccessValid) || (activeStep === 2 && !isRoleValid);

  return (
    <>
      <Modal
        open
        centered
        width={960}
        closable={false}
        footer={null}
        maskClosable={false}
        onCancel={handleClose}
        styles={{body: {padding: 0}}}
      >
        <div style={{display: 'grid', gap: 24, padding: 32}}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16}}>
            <div>
              <Typography.Title level={3} style={{margin: 0}}>
                {selectedUser ? 'Regenerate Code For the User' : 'Register New User'}
              </Typography.Title>
            </div>
            <Button
              type="text"
              onClick={handleClose}
              icon={<CloseOutlined style={{color: '#00857a', fontSize: 18}} />}
            />
          </div>
          <Steps current={activeStep} items={stepItems} />
          <div style={{minHeight: 420}}>{renderStep()}</div>
          <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16}}>
            <Button onClick={handlePrevious}>{activeStep === 0 ? 'Cancel' : 'Previous'}</Button>
            <div>
              {activeStep < 3 ? (
                <Button type="primary" disabled={nextDisabled} onClick={handleNext}>
                  Next
                </Button>
              ) : activeStep === 3 ? (
                currentWebModelIndex === webModels.length - 1 ? (
                  <Button type="primary" onClick={handleNext}>
                    Generate Registration Code
                  </Button>
                ) : (
                  <Button type="primary" onClick={handleNext}>
                    Next
                  </Button>
                )
              ) : (
                <Button type="primary" style={{fontWeight: 'normal'}} loading={submitting} onClick={handleSubmit}>
                  Registration Invite
                </Button>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ExternalUserEnroll;
